import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {ApiErrors, StoreLoadingEnum} from 'types/global';
import {createApiError} from 'helpers/global';
import {EmailLogsDto, EmailLogsQueryParams,} from "types/emailLogs";
import {RootState} from "store";
import {emailLogsApi} from "api/emailLogs";

interface NotificationsEmailLogsDto {
    loading: StoreLoadingEnum;
    error: string | null;
    data: EmailLogsDto | null;
}

const initialState: NotificationsEmailLogsDto = {
    loading: StoreLoadingEnum.idle,
    error: null,
    data: null,
}
export const emailLogsThunk = createAsyncThunk<
    EmailLogsDto,
    EmailLogsQueryParams & { signal?: AbortSignal },
    { rejectValue: ApiErrors }
>(
    'emailLogs/get',
    async (data, thunkAPI) => {
        try {
            return await emailLogsApi.getEmailLogs(data, data.signal);
        } catch (e) {
            if (e.name === 'AbortError' || e.code === 'ERR_CANCELED') {
                return thunkAPI.rejectWithValue({message: 'canceled'});
            }
            return thunkAPI.rejectWithValue(createApiError(e));
        }
    }
);

export const emailLogsSlice = createSlice({
    name: 'emailLogs',
    initialState,
    reducers: {
        reset: (state) => Object.assign(state, initialState),
    },
    extraReducers: (builder) => {
        builder.addCase(emailLogsThunk.pending, (state) => {
            state.data = null;
            state.loading = StoreLoadingEnum.pending;
            state.error = null;
        });
        builder.addCase(emailLogsThunk.fulfilled, (state, action) => {
            state.data = action.payload;
            state.loading = StoreLoadingEnum.loaded;
            state.error = null;
        });
        builder.addCase(emailLogsThunk.rejected, (state, action) => {
            if (!action.payload || action.payload.message === 'canceled') return;

            state.data = null;
            state.loading = StoreLoadingEnum.loaded;
            state.error = action.payload.message ?? null;
        });
    },
});

export const selectEmailLogsDataSelector = (state: RootState) => state.emailLogs.data?.content;
export const selectEmailLogsLoadingSelector = (state: RootState) => state.emailLogs.loading;
export const selectEmailLogsErrorSelector = (state: RootState) => state.emailLogs.error;
export const currentOffsetSelector = (state: RootState) => state.emailLogs.data?.pageable.offset ?? 0;
export const totalElementsSelector = (state: RootState) => state.emailLogs.data?.totalElements ?? 0;
export const currentPageSizeSelector = (state: RootState) => state.emailLogs.data?.pageable.pageSize ?? 0;